/**
 * Centralized KPI value formatters.
 *
 * The backend returns raw decimal values for all formats:
 *   - percent:  0.5268   →  "52.68%"  (multiply by 100, 2 decimal places)
 *   - currency: 149.4    →  "$149.40" (2 decimal places, comma separators)
 *   - number:   177.2    →  "177.20"  (2 decimal places)
 *
 * Special cases:
 *   - INJURY (number)        → whole number (e.g. "10")
 *
 * IMPORTANT: Do NOT apply any pre-multiplication or rounding before calling
 * these helpers. Pass the raw API value directly.
 */

export type KpiFormat = 'percent' | 'currency' | 'currencyDecimal' | 'number' | 'decimal2' | string;

const NULL_DISPLAY = '--';

export function formatPercent(value?: number | null): string {
  if (value === null || value === undefined) {
    return NULL_DISPLAY;
  }
  return `${(value * 100).toFixed(2)}%`;
}

/** Percent with exactly 1 decimal place (e.g. 0.5268 → "52.7%") */
export function formatPercent1dp(value?: number | null): string {
  if (value === null || value === undefined) {
    return NULL_DISPLAY;
  }
  return `${(value * 100).toFixed(1)}%`;
}

/** Percent with exactly 2 decimal places (e.g. 0.1234 → "12.34%") */
export function formatPercent2dp(value?: number | null): string {
  if (value === null || value === undefined) {
    return NULL_DISPLAY;
  }
  return `${(value * 100).toFixed(2)}%`;
}

export function formatCurrency(value?: number | null): string {
  if (value === null || value === undefined) {
    return NULL_DISPLAY;
  }
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absValue);
  return `${sign}$${formatted}`;
}

export function formatCurrencyDecimal(value?: number | null): string {
  if (value === null || value === undefined) {
    return NULL_DISPLAY;
  }
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absValue);
  return `${sign}$${formatted}`;
}

export function formatNumber(value?: number | null): string {
  if (value === null || value === undefined) {
    return NULL_DISPLAY;
  }
  return value.toFixed(2);
}

export function formatDecimal2(value?: number | null): string {
  if (value === null || value === undefined) {
    return NULL_DISPLAY;
  }
  return value.toFixed(2);
}

export function formatKpiValue(
  value: number | null | undefined,
  format: KpiFormat
): string {
  switch (format) {
    case 'percent':
      return formatPercent(value);
    case 'currency':
      return formatCurrency(value);
    case 'currencyDecimal':
      return formatCurrencyDecimal(value);
    case 'decimal2':
      return formatDecimal2(value);
    default:
      return formatNumber(value);
  }
}

/**
 * Name-aware KPI formatter that applies per-KPI decimal precision rules:
 *   - "INJURY" / "INJURIES"  → whole number (e.g. "10")
 *   - all other KPIs         → 2 decimal places with appropriate prefix/suffix
 */
export function formatKpiValueByName(
  value: number | null | undefined,
  format: KpiFormat,
  metricName: string
): string {
  if (value === null || value === undefined) {
    return NULL_DISPLAY;
  }

  const upper = metricName.trim().toUpperCase();

  // Injury → whole number (no decimals)
  if (upper === 'INJURY' || upper === 'INJURIES') {
    return String(Math.round(value));
  }

  // PKU % and SIA % → always format as percent (2 decimal places)
  // The API may return these with format 'number' but they are percentages
  if (upper.includes('PKU') || upper.includes('SIA')) {
    return formatPercent2dp(value);
  }

  // All others → delegate to format-based formatter (all use 2dp)
  return formatKpiValue(value, format);
}

// ─── 1dp helpers (for targets) ────────────────────────────────────────────────

/** Currency with 1 decimal place (e.g. 149.4 → "$149.4") */
export function formatCurrency1dp(value?: number | null): string {
  if (value === null || value === undefined) {
    return NULL_DISPLAY;
  }
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(absValue);
  return `${sign}$${formatted}`;
}

/** Number with 1 decimal place (e.g. 177.23 → "177.2") */
export function formatNumber1dp(value?: number | null): string {
  if (value === null || value === undefined) {
    return NULL_DISPLAY;
  }
  return value.toFixed(1);
}

/**
 * Target-specific KPI formatter.
 * Rules:
 *   - "INJURY" / "INJURIES"  → whole number (same as actual)
 *   - "IN HOUSE CUT"         → 2dp (exception — keeps full precision)
 *   - Gap Time                 → 2dp by default
 *   - other targets           → 1dp by default
 */
export function formatKpiTargetByName(
  value: number | null | undefined,
  format: KpiFormat,
  metricName: string,
  decimalPlaces: 1 | 2 = metricName.trim().toUpperCase().startsWith('GAP TIME') ? 2 : 1
): string {
  if (value === null || value === undefined) {
    return NULL_DISPLAY;
  }

  const upper = metricName.trim().toUpperCase();

  // Injury → whole number (no decimals)
  if (upper === 'INJURY' || upper === 'INJURIES') {
    return String(Math.round(value));
  }

  // In House Cut → keep 2dp (exception)
  if (upper.includes('IN HOUSE CUT')) {
    return formatKpiValueByName(value, format, metricName);
  }

  // PKU % and SIA % → percent at the requested target precision
  if (upper.includes('PKU') || upper.includes('SIA')) {
    return decimalPlaces === 1 ? formatPercent1dp(value) : formatPercent2dp(value);
  }

  // All others use the requested target precision.
  switch (format) {
    case 'percent':
      return decimalPlaces === 1 ? formatPercent1dp(value) : formatPercent2dp(value);
    case 'currency':
    case 'currencyDecimal':
      if (decimalPlaces === 1) return formatCurrency1dp(value);
      return formatCurrencyDecimal(value);
    default:
      return decimalPlaces === 1 ? formatNumber1dp(value) : formatNumber(value);
  }
}
